// Persona reportada. Corresponde a la tabla "Personas" del Excel.
//
// Estos schemas son la whitelist contra mass assignment: el backend construye
// el `data` de Prisma desde el resultado de .parse(), nunca desde req.body.

import { z } from "zod";
import {
  fechaPasada,
  punto,
  reportanteSchema,
  textoLibre,
  TIPO_REPORTE,
} from "./comunes.js";

export const SEXO = ["HOMBRE", "MUJER", "NO_ESPECIFICA"];
export const CONTEXTURA = ["DELGADA", "MEDIANA", "ROBUSTA"];
export const TEZ_PIEL = ["BLANCA", "TRIGUENA", "OSCURA"];
export const COLOR_CABELLO = [
  "NEGRO",
  "CASTANO_OSCURO",
  "CASTANO",
  "CASTANO_CLARO",
  "RUBIO",
  "RUBIO_OSCURO",
  "PELIRROJO",
  "CANOSO",
  "TENIDO",
  "CALVO",
];
export const COLOR_OJOS = [
  "MARRON_OSCURO",
  "MARRON",
  "MIEL",
  "VERDE",
  "AZUL",
  "GRIS",
  "NEGRO",
  "HETEROCROMIA",
  "NO_ESPECIFICA",
];
export const RELACION = ["FAMILIAR", "AMIGO", "COLEGA", "DESCONOCIDO"];
export const ESTADO_PERSONA = ["BIEN", "HERIDA", "DESORIENTADA"];
export const LUGAR_ENCONTRADO = [
  "HOSPITAL",
  "VIA_PUBLICA",
  "LUGAR_PUBLICO",
  "OTRO",
];

const camposPersona = {
  nombrePersona: textoLibre(80),
  sexo: z.enum(SEXO),
  relacion: z.enum(RELACION),

  edadAproximada: z.number().int().min(0).max(120).optional(),
  estaturaAproximada: z.number().min(0.3).max(2.6).optional(), // metros
  contextura: z.enum(CONTEXTURA).optional(),
  tezPiel: z.enum(TEZ_PIEL).optional(),
  colorCabello: z.enum(COLOR_CABELLO).optional(),
  /**
   * Colores observados en el cabello.
   *
   * MySQL guarda esto como JSON, y un JSON no valida nada por si mismo: sin
   * este schema entrarian `["morado"]` o `[123]` y la columna los aceptaria.
   * Toda la garantia esta aca.
   *
   * Se quitan duplicados antes de validar y se conserva el orden en que los
   * escribio quien reporta: el primero es el predominante.
   */
  coloresCabello: z
    .array(z.enum(COLOR_CABELLO))
    .transform((lista) => [...new Set(lista)])
    .pipe(
      z
        .array(z.enum(COLOR_CABELLO))
        .min(1, "Indica al menos un color de cabello")
        // Tres es lo que alguien puede describir de memoria con utilidad. Mas
        // colores no ayudan a reconocer a nadie y ensucian el filtro.
        .max(3, "Maximo 3 colores de cabello"),
    )
    .refine((lista) => !(lista.includes("CALVO") && lista.length > 1), {
      message: "CALVO no se combina con otros colores",
    })
    .optional(),
  colorOjos: z.enum(COLOR_OJOS).optional(),
  senalesParticulares: textoLibre(255).optional(),
  vestimenta: textoLibre(255).optional(),
  informacionAdicional: textoLibre(1000).optional(),
  estadoPersona: z.enum(ESTADO_PERSONA).optional(),

  ultimaVistaEn: fechaPasada.optional(),
  encontradaEn: fechaPasada.optional(),

  lugarEncontrado: z.enum(LUGAR_ENCONTRADO).optional(),
  nombreHospital: textoLibre(120).optional(),

  // Ubicacion legible para las tarjetas del listado. La manda el frontend
  // desde addressdetails del geocodificador; el texto largo va aparte.
  ciudad: textoLibre(80).optional(),
  departamento: textoLibre(60).optional(),
  perdido: punto.optional(),
  encontro: punto.optional(),
};

export const crearPersonaSchema = z
  .object({
    tipo: z.enum(TIPO_REPORTE),
    reportante: reportanteSchema,
    ...camposPersona,
  })
  .superRefine((valor, ctx) => {
    // Un reporte sin ninguna pista de donde buscar no le sirve a nadie: no se
    // puede pintar en el mapa ni acotar la busqueda.
    if (!valor.perdido && !valor.encontro) {
      ctx.addIssue({
        code: "custom",
        path: ["perdido"],
        message: "Indica al menos un lugar: donde se perdio o donde se encontro",
      });
    }
    if (valor.tipo === "ENCONTRADO" && !valor.encontro) {
      ctx.addIssue({
        code: "custom",
        path: ["encontro"],
        message: "Un reporte de persona encontrada necesita el lugar del hallazgo",
      });
    }
    if (valor.tipo === "ENCONTRADO" && !valor.estadoPersona) {
      ctx.addIssue({
        code: "custom",
        path: ["estadoPersona"],
        message: "Indica el estado de la persona encontrada",
      });
    }
    if (valor.tipo === "ENCONTRADO" && !valor.lugarEncontrado) {
      ctx.addIssue({
        code: "custom",
        path: ["lugarEncontrado"],
        message: "Indica el tipo de lugar donde encontraste a la persona",
      });
    }

    // colorCabello es el predominante y coloresCabello la lista completa. Si
    // llegan los dos y no concuerdan, el reporte se contradice a si mismo: el
    // filtro por color diria una cosa y la ficha mostraria otra.
    if (
      valor.colorCabello &&
      valor.coloresCabello &&
      !valor.coloresCabello.includes(valor.colorCabello)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["colorCabello"],
        message: "El color predominante debe estar entre los colores indicados",
      });
    }
  });

/// Editar no permite cambiar `tipo` ni el reportante: eso seria otro reporte.
export const editarPersonaSchema = z.object(camposPersona).partial();
